import React from 'react';
import { WidgetHeaderIcon } from '@/shared/components/ui/WidgetHeaderIcon';
import type { IconName } from '@/shared/icons'
interface TrendData {
  previousPeriod: number;
  change: number;
  direction: 'up' | 'down' | 'stable';
}

interface DashboardSummaryCardProps {
  title: string;
  value: number | string;
  icon: IconName;
  trend?: TrendData;
  subtitle?: string;
}

/**
 * Card de resumo para relatórios CRM
 *
 * Segue o padrão visual do MetricCard da dashboard principal:
 * - WidgetHeaderIcon (sempre roxo/primary, padrão unificado)
 * - Título uppercase tracking-wide
 * - Valor com tabular-nums e tracking-tight
 * - Delta badge com pill colorida
 */
export const DashboardSummaryCard: React.FC<DashboardSummaryCardProps> = ({
  title,
  value,
  icon,
  trend,
  subtitle,
}) => {

  const getTrendConfig = () => {
    if (!trend) return null;
    switch (trend.direction) {
      case 'up':
        return {
          color: 'var(--theme-success-color)',
          bgColor: 'var(--theme-success-background, rgba(52, 211, 153, 0.1))',
          TrendIcon: 'arrow-up-right',
        };
      case 'down':
        return {
          color: 'var(--theme-error-color)',
          bgColor: 'var(--theme-error-background, rgba(248, 113, 113, 0.1))',
          TrendIcon: 'arrow-down-right',
        };
      default:
        return {
          color: 'var(--theme-muted-text)',
          bgColor: 'transparent',
          TrendIcon: 'minus',
        };
    }
  };

  const trendConfig = getTrendConfig();
  const displayPercent = trend
    ? `${trend.change > 0 ? '+' : ''}${trend.change.toFixed(1)}%`
    : null;

  return (
    <div
      className="rounded-lg border flex flex-col"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header: Title + Icon */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <h3
          className="text-xs font-medium uppercase tracking-wide truncate"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {title}
        </h3>
        <WidgetHeaderIcon icon={icon} />
      </div>

      {/* Value + Trend (mockup `.kpi`): valor 26px/800 e pill de tendência em LINHA PRÓPRIA abaixo */}
      <div className="px-4 pb-1">
        <div
          className="text-[26px] font-extrabold tracking-[-0.02em] mt-1.5"
          style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </div>

        {/* Pill de tendência — linha própria, radius 999, seta textual '▲/▼' */}
        {trendConfig && displayPercent && (
          <span
            className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[11px] font-bold mt-1.5"
            style={{ color: trendConfig.color, backgroundColor: trendConfig.bgColor }}
          >
            {String(trendConfig.TrendIcon).includes('up') ? '▲' : '▼'}
            {displayPercent}
          </span>
        )}
      </div>

      {/* Subtitle / Comparison */}
      <div className="px-4 pb-3 pt-1">
        {subtitle && (
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

DashboardSummaryCard.displayName = 'DashboardSummaryCard';
